import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { usePrefersReducedMotion } from '@/hooks/useMediaQuery';
import { EASE_OUT, DUR } from './motion';
import FaIcon from './FaIcon';
import Button from './Button';

type EmptyStateSize = 'sm' | 'md' | 'lg';

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
}

interface EmptyStateProps {
  /** Heading level for the title. Defaults to 3; pass 2 when this replaces a page's content. */
  headingLevel?: 2 | 3 | 4;
  icon?: string;
  iconNode?: ReactNode;
  title: string;
  description?: string;
  /** Simple CTA button label */
  actionLabel?: string;
  /** Simple CTA button handler */
  onAction?: () => void;
  /** Icon rendered inside the CTA button */
  actionIcon?: ReactNode;
  /** Structured action object (alternative to actionLabel/onAction) */
  action?: EmptyStateAction;
  /** Size variant: sm for inline/compact, md (default), lg for full-page */
  size?: EmptyStateSize;
  /** Visual variant: 'default' (gradient circle) or 'operational' (flat inset square, no decoration). */
  variant?: 'default' | 'operational';
  className?: string;
  /** @deprecated Use size="sm" instead */
  compact?: boolean;
}

// Size configuration
const sizeConfig: Record<EmptyStateSize, {
  padding: string;
  iconRing: string;
  iconSize: string;
  iconGap: string;
  titleClass: string;
  descClass: string;
  descGap: string;
  actionGap: string;
}> = {
  sm: {
    padding: 'py-6',
    iconRing: 'w-10 h-10',
    iconSize: 'h-5 w-5',
    iconGap: 'mb-2',
    titleClass: 'text-sm',
    descClass: 'text-xs',
    descGap: 'mt-1',
    actionGap: 'mt-3',
  },
  md: {
    padding: 'py-12',
    iconRing: 'w-14 h-14',
    iconSize: 'h-7 w-7',
    iconGap: 'mb-4',
    titleClass: 'text-base',
    descClass: 'text-sm',
    descGap: 'mt-1.5',
    actionGap: 'mt-5',
  },
  lg: {
    padding: 'py-16',
    iconRing: 'w-18 h-18',
    iconSize: 'h-8 w-8',
    iconGap: 'mb-5',
    titleClass: 'text-lg',
    descClass: 'text-sm',
    descGap: 'mt-2',
    actionGap: 'mt-6',
  },
};

// Animation variants
const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

// Motion doctrine: one easing family, no springs (see components/common/motion.ts).
const iconVariants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: DUR.standard, ease: EASE_OUT },
  },
};

const fadeUpVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: DUR.orchestration, ease: EASE_OUT },
  },
};

/**
 * Polished empty state component with entrance animations and gradient icon.
 *
 * Renders a centered layout with:
 * - Large icon in a gradient-tinted circle
 * - Title in Outfit heading font
 * - Optional description in muted text
 * - Optional CTA button
 * - Staggered Framer Motion entrance (respects prefers-reduced-motion)
 *
 * @example
 * <EmptyState
 *   icon="folder-open"
 *   title="No files found"
 *   description="Upload a file to get started."
 *   actionLabel="Upload File"
 *   onAction={() => setShowUpload(true)}
 * />
 *
 * @example
 * <EmptyState
 *   icon="clipboard-list"
 *   title="No tasks yet"
 *   size="lg"
 *   action={{ label: 'Create Task', onClick: () => setShowModal(true) }}
 * />
 */
export default function EmptyState({
  headingLevel = 3,
  icon,
  iconNode,
  title,
  description,
  actionLabel,
  onAction,
  actionIcon,
  action,
  size,
  variant = 'default',
  className = '',
  compact = false,
}: EmptyStateProps) {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  const prefersReducedMotion = usePrefersReducedMotion();

  // Determine effective size: explicit size prop > compact legacy prop > default 'md'
  const effectiveSize: EmptyStateSize = size ?? (compact ? 'sm' : 'md');
  const config = sizeConfig[effectiveSize];

  // Resolve the action — structured `action` prop takes precedence
  const resolvedAction = action ?? (actionLabel && onAction ? { label: actionLabel, onClick: onAction, icon: actionIcon } : null);

  const content = (
    <>
      {/* Icon circle with primary gradient tint */}
      {(icon || iconNode) && (
        <MaybeMotion
          reduced={prefersReducedMotion}
          variants={iconVariants}
          className={`${config.iconGap} flex items-center justify-center ${config.iconRing} ${
            variant === 'operational'
              ? 'rounded-md bg-surface-inset text-theme-tertiary'
              : 'rounded-full bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-950/40 dark:to-primary-900/30 ring-1 ring-primary-200/50 dark:ring-primary-800/30'
          }`}
        >
          {iconNode || (
            <FaIcon
              icon={icon!}
              className={`${config.iconSize} ${variant === 'operational' ? 'text-theme-tertiary' : 'text-primary-500 dark:text-primary-400'}`}
            />
          )}
        </MaybeMotion>
      )}

      {/* Title */}
      <MaybeMotion reduced={prefersReducedMotion} variants={fadeUpVariants}>
        {/*
          The level is the caller's, because it depends on where this sits. An
          empty state that replaces a page's whole content belongs one level
          under the page <h1>; one inside a card belongs under that card's
          heading. Defaults to h3, which is what all 153 call sites render
          today, so nothing moves unless a caller asks.
        */}
        <Heading className={`${config.titleClass} font-heading font-semibold text-theme-primary`}>
          {title}
        </Heading>
      </MaybeMotion>

      {/* Description */}
      {description && (
        <MaybeMotion reduced={prefersReducedMotion} variants={fadeUpVariants}>
          <p className={`${config.descGap} ${config.descClass} text-theme-tertiary max-w-sm`}>
            {description}
          </p>
        </MaybeMotion>
      )}

      {/* CTA Button */}
      {resolvedAction && (
        <MaybeMotion reduced={prefersReducedMotion} variants={fadeUpVariants}>
          <div className={config.actionGap}>
            <Button
              variant="primary"
              size="sm"
              onClick={resolvedAction.onClick}
              icon={resolvedAction.icon}
            >
              {resolvedAction.label}
            </Button>
          </div>
        </MaybeMotion>
      )}
    </>
  );

  if (prefersReducedMotion) {
    return (
      <div className={`flex flex-col items-center justify-center text-center ${config.padding} ${className}`}>
        {content}
      </div>
    );
  }

  return (
    <motion.div
      className={`flex flex-col items-center justify-center text-center ${config.padding} ${className}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {content}
    </motion.div>
  );
}

/**
 * Conditionally wraps children in a motion.div for animation, or a plain div
 * when the user prefers reduced motion.
 */
function MaybeMotion({
  reduced,
  variants,
  className,
  children,
}: {
  reduced: boolean;
  variants: typeof iconVariants | typeof fadeUpVariants;
  className?: string;
  children: ReactNode;
}) {
  if (reduced) {
    return className ? <div className={className}>{children}</div> : <>{children}</>;
  }
  return (
    <motion.div variants={variants} className={className}>
      {children}
    </motion.div>
  );
}
